/**
 * Module dependencies.
 */

import { Category, Wrapper } from './portfolio-categories.styled';
import { FadeInUp } from 'components/atoms/in-view-once/fade-in-up';
import { InViewOnce } from 'components/atoms/in-view-once/in-view-once';
import { JSX, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { PortfolioCategory } from 'types/portfolio';
import { find } from 'lodash';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

/**
 * `Props` type.
 */

type Props = {
  children: (
    data: PortfolioCategory | null,
    category: string | string[] | undefined,
  ) => ReactNode;
  portfolioCategories: Array<PortfolioCategory>;
};

/**
 * Export `PortfolioCategories` component.
 */

export function PortfolioCategories(props: Props): JSX.Element {
  const { children, portfolioCategories } = props;
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const router = useRouter();
  const { asPath, locale, pathname, query } = router;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [justify, setJustify] = useState('center');
  const [currentCategory, setCurrentCategory] = useState<
    string | string[] | undefined
  >();

  useEffect(() => {
    setCurrentCategory(query.category);
  }, [query.category]);

  const data = useMemo(() => {
    if (!currentCategory) {
      return null;
    }

    return find(portfolioCategories, { id: currentCategory });
  }, [currentCategory, portfolioCategories]);

  useEffect(() => {
    const checkScroll = () => {
      const wrapper = wrapperRef.current;

      if (wrapper) {
        const hasScroll = wrapper.scrollWidth > wrapper.clientWidth;

        setJustify(hasScroll ? 'flex-start' : 'center');
      }
    };

    // Verificar no mount e em resize
    checkScroll();
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  useEffect(() => {
    if (!query.category) {
      router.push(
        {
          pathname,
          query: { ...query, category: portfolioCategories?.[0]?.id },
        },
        undefined,
        {
          shallow: true,
        },
      );
    }
  }, [asPath, locale, pathname, portfolioCategories, query, router]);

  return (
    <>
      <Wrapper
        ref={wrapperRef}
        style={{
          justifyContent: justify,
        }}
      >
        {portfolioCategories.map((portfolioCategory) => (
          <InViewOnce AnimationComponent={FadeInUp} key={portfolioCategory.id}>
            <Category
              active={String(portfolioCategory.id) === currentCategory}
              key={portfolioCategory.id}
              onClick={() => {
                setCurrentCategory(String(portfolioCategory.id));
              }}
            >
              {portfolioCategory.title[lang]}
            </Category>
          </InViewOnce>
        ))}
      </Wrapper>

      {children && children(data, currentCategory)}
    </>
  );
}
